/**
 * ListingMedia Component
 *
 * Diamond Architecture: Responsive wrapper that shows ImageMosaic on desktop, carousel on mobile.
 * Manages shared PhotoGalleryModal state.
 *
 * Both layouts are server-rendered and CSS picks one (`hidden md:block` /
 * `md:hidden`), so the server's markup and the client's are the same and
 * nothing guesses a viewport. CSS hiding does not stop a fetch, though, so each
 * layout is told the media at which it is the VISIBLE one: its photo-0 preload
 * is scoped to that media and its `<img>`s stay lazy, and the browser's own
 * media evaluation fetches photo 0 once per open (#9021: a desktop open used
 * to fetch it twice — 2000px for the mosaic and 2400px for the hidden carousel).
 */
import type { LocalizedText, S3Key } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { useState } from 'react';

import { ImageMosaic } from './ImageMosaic';
import { ListingGallery } from './ListingGallery';
import { PhotoGalleryModal } from './PhotoGalleryModal';

/** Where the mosaic is visible — Tailwind's `md` breakpoint, the `md:block` below. */
const DESKTOP_MEDIA = '(min-width: 768px)';
/**
 * Where the carousel is visible — the `md:hidden` below. Spelled as the EXACT
 * complement of Tailwind's `md` (`min-width: 768px`), not as
 * `(max-width: 767px)`: at a fractional viewport width (767.5px, from zoom or
 * a device pixel ratio) the latter matches neither query, so neither layout
 * would preload. `not all and (…)` matches precisely where `md` does not.
 */
const MOBILE_MEDIA = `not all and ${DESKTOP_MEDIA}`;

interface ListingMediaProps {
  images: S3Key[];
  alt: LocalizedText | '';
  className?: string;
}

export function ListingMedia({ images, alt, className }: ListingMediaProps) {
  const [modalOpen, setModalOpen] = useState(false);

  if (!images.length) {
    return null;
  }

  const handleViewAll = () => {
    setModalOpen(true);
  };

  return (
    <>
      {/* Desktop: Mosaic Grid */}
      <div data-testid="listing-media" className={cn('hidden md:block', className)}>
        <ImageMosaic
          images={images}
          alt={alt}
          onViewAll={handleViewAll}
          visibleMedia={DESKTOP_MEDIA}
        />
      </div>

      {/* Mobile: Carousel */}
      <div data-testid="listing-media" className={cn('md:hidden', className)}>
        <ListingGallery
          images={images}
          alt={alt}
          onImageClick={handleViewAll}
          visibleMedia={MOBILE_MEDIA}
        />
      </div>

      {/* Shared Fullscreen Modal */}
      <PhotoGalleryModal images={images} alt={alt} open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}
